import React, { useState, useEffect, Suspense, lazy } from 'react';
import KpiCardsSection from './KpiCardsSection';
import RecentRuns from './RecentRuns';
import OpenAlerts from './OpenAlerts';
import ApiKeyCTA from './ApiKeyCTA';
import RunSampleTestCTA from './RunSampleTestCTA';
import UsageMeter from './UsageMeter';
import OnboardingChartPlaceholder from './OnboardingChartPlaceholder';

// Lazy load heavy chart component
const AreaChart = lazy(() => import('./AreaChart'));

// Loading component for charts
const ChartLoader = () => (
  <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
  </div>
);

import { isFeatureEnabled } from '@/lib/analytics';
import { Run } from '@/features/runs/types';
import { DataPoint } from './chart/chartConstants';
import { apiClient } from '@/lib/api/client';
import { getFailuresOverTime } from '@/api/usage';
import { useOrganization } from '@/lib/contexts/OrganizationContext';

interface Alert {
  id: string;
  severity: 'high' | 'medium' | 'low';
  message: string;
  age: string;
}

interface TestRunApiData {
  id: string;
  status: string;
  suite: string;
  date_time: string;
  runtime: number | null;
  details: string | {
    commit_hash?: string;
    trigger?: string;
    environment?: string;
    author?: string;
  };
  created_at: string;
  updated_at: string;
}

interface TestRunsApiResponse {
  data: TestRunApiData[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
  };
}

interface AlertApiData {
  id: string;
  rule_id: string;
  severity: string;
  status: string;
  triggered_at: string;
  value: string | null;
  message: string;
  runs: string;
  created_at: string;
}

interface AlertsApiResponse {
  data: AlertApiData[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
  };
}

// Helper function to calculate time ago
const getTimeAgo = (date: Date): string => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  
  let interval = seconds / 31536000;
  if (interval > 1) return Math.floor(interval) + " years ago";
  
  interval = seconds / 2592000;
  if (interval > 1) return Math.floor(interval) + " months ago";
  
  interval = seconds / 86400;
  if (interval > 1) return Math.floor(interval) + " days ago";
  
  interval = seconds / 3600;
  if (interval > 1) return Math.floor(interval) + " hours ago";
  
  interval = seconds / 60;
  if (interval > 1) return Math.floor(interval) + " minutes ago";
  
  return Math.floor(seconds) + " seconds ago";
};

interface DashboardContentProps {
  onRunClick: (id: string) => void;
  onAlertClick: (id: string) => void;
  onAcknowledgeAlert: (id: string) => void;
  onCreateTicket: (id: string) => void;
}

const DashboardContent: React.FC<DashboardContentProps> = ({
  onRunClick,
  onAlertClick,
  onAcknowledgeAlert,
  onCreateTicket
}) => {
  const { organization } = useOrganization();
  const [recentRuns, setRecentRuns] = useState<Run[]>([]);
  const [isRunsLoading, setIsRunsLoading] = useState(true);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [isAlertsLoading, setIsAlertsLoading] = useState(true);
  const [chartData, setChartData] = useState<DataPoint[]>([]);
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [chartPeriod, setChartPeriod] = useState('7d');
  
  // Pagination defaults
  const DEFAULT_PAGINATION = { total: 0, page: 1, limit: 5 } as const;
  
  // New state for pagination
  const [runsPagination, setRunsPagination] = useState(DEFAULT_PAGINATION);
  const [alertsPagination, setAlertsPagination] = useState(DEFAULT_PAGINATION);

  useEffect(() => {
    // Performance timing for debugging
    const startTime = performance.now();
    
    // Fetch recent runs with timing
    const fetchRecentRuns = async () => {
      const apiStartTime = performance.now();
      setIsRunsLoading(true);
      try {
        const response = await apiClient.get<TestRunsApiResponse>('/api/runs', {
          params: {
            page: runsPagination.page,
            limit: runsPagination.limit,
            sort: 'createdAt:desc'
          }
        });
        
        if (import.meta.env.DEV) {
          console.log(`[Dashboard] Runs API took ${performance.now() - apiStartTime}ms`);
        }
        
        if (response.success && response.data) {
          if (response.data.pagination) {
            setRunsPagination(response.data.pagination);
          }
          
          setRecentRuns((response.data.data || []).map((run) => ({
            id: run.id,
            status: run.status as 'passed' | 'failed' | 'warning',
            suite: run.suite,
            commit: typeof run.details === 'object' ? run.details.commit_hash || 'unknown' : 'unknown',
            trigger: typeof run.details === 'object' ? run.details.trigger as 'CI' | 'manual' || 'manual' : 'manual',
            env: typeof run.details === 'object' ? run.details.environment as 'prod' | 'staging' | 'dev' || 'dev' : 'dev',
            author: typeof run.details === 'object' ? run.details.author || 'unknown' : 'unknown',
            timestamp: run.created_at ? new Date(run.created_at).toLocaleString() : 'unknown',
            dateTime: run.date_time || '',
            runtime: run.runtime ? `${Math.floor(run.runtime / 60)}m ${run.runtime % 60}s` : '0m 0s'
          })));
        } else {
          setRecentRuns([]);
          setRunsPagination(DEFAULT_PAGINATION);
        }
      } catch (error) {
        console.error('Error fetching recent runs:', error);
        setRecentRuns([]);
        setRunsPagination(DEFAULT_PAGINATION);
      } finally {
        setIsRunsLoading(false);
      }
    };

    // Fetch open alerts with timing
    const fetchOpenAlerts = async () => {
      const apiStartTime = performance.now();
      setIsAlertsLoading(true);
      try {
        const response = await apiClient.get<AlertsApiResponse>('/api/alerts', {
          params: {
            status: 'open',
            limit: alertsPagination.limit,
            sort: 'created_at:desc'
          }
        });
        
        if (import.meta.env.DEV) {
          console.log(`[Dashboard] Alerts API took ${performance.now() - apiStartTime}ms`);
        }
        
        if (response.success && response.data) {
          if (response.data.pagination) {
            setAlertsPagination(response.data.pagination);
          }
          
          setAlerts((response.data.data || []).map((alert) => ({
            id: alert.id,
            severity: alert.severity as 'high' | 'medium' | 'low',
            message: alert.message,
            age: alert.created_at ? getTimeAgo(new Date(alert.created_at)) : 'unknown'
          })));
        } else {
          setAlerts([]);
          setAlertsPagination(DEFAULT_PAGINATION);
        }
      } catch (error) {
        console.error('Error fetching open alerts:', error);
        setAlerts([]);
        setAlertsPagination(DEFAULT_PAGINATION);
      } finally {
        setIsAlertsLoading(false);
      }
    };

    // Fetch chart data with timing
    const fetchChartData = async () => {
      const apiStartTime = performance.now();
      setIsChartLoading(true);
      try {
        const response = await getFailuresOverTime({
          period: chartPeriod
        });
        
        if (import.meta.env.DEV) {
          console.log(`[Dashboard] Chart API took ${performance.now() - apiStartTime}ms`);
        }
        
        if (response.success && response.data) {
          const transformedData: DataPoint[] = (response.data || []).map((point) => ({
            date: point.date,
            failures: point.failures,
            dateObj: point.dateObj || new Date(point.date)
          }));
          setChartData(transformedData);
        } else {
          console.error('Failed to fetch failures over time data:', response.error);
          setChartData([]);
        }
      } catch (error) {
        console.error('Error fetching chart data:', error);
        setChartData([]);
      } finally {
        setIsChartLoading(false);
      }
    };

    // Load all dashboard data in parallel (don't wait for organization)
    Promise.all([
      fetchRecentRuns(),
      fetchOpenAlerts(),
      fetchChartData()
    ]).then(() => {
      if (import.meta.env.DEV) {
        console.log(`[Dashboard] Total load time: ${performance.now() - startTime}ms`);
      }
    }).catch(error => {
      console.error('Error loading dashboard data:', error);
    });
  }, [chartPeriod]); // Remove organization dependency

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Dashboard</h1>
      </div>
      
      {/* KPI Cards */}
      <KpiCardsSection />

      {/* Usage Meter - Moved Up */}
      <UsageMeter variant="full-width" />

      {/* CTA Section - API Keys and Sample Test */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ApiKeyCTA />
        <RunSampleTestCTA />
      </div>

      {/* Failures Over Time Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Failures Over Time</h2>
            <select 
              value={chartPeriod} 
              onChange={(e) => setChartPeriod(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
          </div>
        </div>
        {isChartLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        ) : chartData.length === 0 ? (
          <div className="p-6">
            <OnboardingChartPlaceholder />
          </div>
        ) : (
          <Suspense fallback={<ChartLoader />}>
            <AreaChart 
              title="" 
              data={chartData}
            />
          </Suspense>
        )}
      </div>

      {/* Recent Runs & Open Alerts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          {isRunsLoading ? (
            <div className="flex justify-center items-center py-12 bg-white dark:bg-gray-800 rounded-md shadow">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
            </div>
          ) : recentRuns.length === 0 ? (
            <div className="flex justify-center items-center py-12 bg-white dark:bg-gray-800 rounded-md shadow">
              <p className="text-muted-foreground">No recent runs available.</p>
            </div>
          ) : (
            <div>
              <RecentRuns 
                runs={recentRuns} 
                onRowClick={onRunClick} 
              />
              {/* Display pagination information if available */}
              {runsPagination.total > runsPagination.limit && (
                <div className="mt-2 text-sm text-gray-500 text-right">
                  Showing {Math.min(runsPagination.limit, recentRuns.length)} of {runsPagination.total} runs
                </div>
              )}
            </div>
          )}
        </div>
        <div className="space-y-6">
          {isAlertsLoading ? (
            <div className="flex justify-center items-center py-12 bg-white dark:bg-gray-800 rounded-md shadow">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
            </div>
          ) : alerts.length === 0 ? (
            <div className="flex justify-center items-center py-12 bg-white dark:bg-gray-800 rounded-md shadow">
              <p className="text-muted-foreground">No open alerts available.</p>
            </div>
          ) : (
            <div>
              <OpenAlerts 
                alerts={alerts} 
                onAlertClick={onAlertClick} 
                onAcknowledgeAlert={onAcknowledgeAlert}
                onCreateTicket={onCreateTicket}
              />
              {/* Display pagination information if available */}
              {alertsPagination.total > alertsPagination.limit && (
                <div className="mt-2 text-sm text-gray-500 text-right">
                  Showing {Math.min(alertsPagination.limit, alerts.length)} of {alertsPagination.total} alerts
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardContent;
